import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  Plus, Trash2, Eye, X, Search, FileSpreadsheet, Send, CheckCircle, XCircle, Save,
} from "lucide-react";
import { api, peso } from "../../lib/api";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, BrutalTextarea, Badge, SectionTitle,
} from "../../components/common/Brutal";

const STATUS_COLORS = { unconfirmed: "amber", confirmed: "green", cancelled: "red" };

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [zones, setZones] = useState([]);
  const [channels, setChannels] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ subject: "", message: "" });
  const [cancelForm, setCancelForm] = useState({ subject: "", reason: "" });

  const load = async () => {
    const [b, z, c] = await Promise.all([api.get("/bookings"), api.get("/zones"), api.get("/payment-channels")]);
    setBookings(b.data); setZones(z.data); setChannels(c.data);
  };

  useEffect(() => { load(); }, []);

  const channelLabel = (id) => {
    const c = channels.find((x) => x.id === id);
    return c ? `${c.method.toUpperCase()} · ${c.account_name}` : "-";
  };

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (typeFilter !== "all" && b.camping_type !== typeFilter) return false;
      if (zoneFilter !== "all" && b.zone_id !== zoneFilter) return false;
      if (channelFilter !== "all" && b.payment_channel_id !== channelFilter) return false;
      if (statusFilter !== "all" && b.payment_status !== statusFilter) return false;
      if (dateFrom) {
        const d = new Date(b.created_at);
        if (d < new Date(dateFrom)) return false;
      }
      if (dateTo) {
        const d = new Date(b.created_at);
        if (d > new Date(dateTo + "T23:59:59")) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const primary = b.persons?.[0] || {};
        const blob = [b.booking_id, b.id, primary.name, b.user_email, primary.email, primary.mobile_number, b.user_mobile].filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [bookings, typeFilter, zoneFilter, channelFilter, statusFilter, dateFrom, dateTo, search]);

  const exportXlsx = () => {
    const rows = filtered.map((b) => {
      const p = b.persons?.[0] || {};
      return {
        "Booking ID": b.booking_id || b.id,
        "Primary Camper": p.name || "",
        "Type": b.camping_type,
        "Zone": b.zone_name,
        "Persons": b.persons.length,
        "Email": p.email || b.user_email,
        "Mobile": p.mobile_number || b.user_mobile,
        "Date of Booking": new Date(b.created_at).toLocaleString(),
        "Payment Channel": channelLabel(b.payment_channel_id),
        "Payment Status": b.payment_status,
        "Amount Paid": b.amount_paid || "",
        "Total": b.total_amount,
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");
    XLSX.writeFile(wb, `kaogma-bookings-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const openDetails = (b) => {
    setSelected(b);
    setContactForm({ subject: `KAOGMA Festival — Booking ${b.booking_id}`, message: "" });
    setCancelForm({ subject: `RESERVATION CANCELLATION: ${b.booking_id}`, reason: "" });
  };

  const deleteBooking = async (b) => {
    try {
      await api.delete(`/bookings/${b.id}`);
      toast.success("Booking deleted (camper notified, QR invalidated)");
      setDeleteTarget(null);
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const sendContact = async () => {
    if (!selected) return;
    try {
      await api.post(`/bookings/${selected.id}/contact`, contactForm);
      toast.success(`Email sent to ${selected.user_email}`);
      setContactOpen(false);
    } catch { toast.error("Failed"); }
  };

  const confirmPayment = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/bookings/${selected.id}/confirm-payment`);
      toast.success("Payment confirmed — QR codes now visible to camper. Confirmation email sent.");
      setConfirmOpen(false);
      setSelected(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancelReservation = async () => {
    if (!selected) return;
    if (!cancelForm.reason) return toast.error("Reason required");
    try {
      const { data } = await api.post(`/bookings/${selected.id}/cancel-with-reason`, cancelForm);
      toast.success(`Cancellation email sent to ${selected.user_email}`);
      setCancelOpen(false);
      setSelected(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionTitle kicker="Operations" title="All Bookings" />
        <div className="flex gap-2">
          <BrutalButton variant="secondary" onClick={exportXlsx} data-testid="export-xlsx"><FileSpreadsheet className="h-4 w-4" /> Export Excel</BrutalButton>
          <BrutalButton variant="primary" onClick={() => setAddOpen(true)} data-testid="add-booking-btn"><Plus className="h-4 w-4" /> Add Booking</BrutalButton>
        </div>
      </div>

      <BrutalCard className="p-4 grid md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="lg:col-span-2 relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B4E9E]" />
          <BrutalInput className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search booking id, camper, email, mobile..." data-testid="bookings-search" />
        </div>
        <select className="bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="filter-type">
          <option value="all">All Types</option>
          <option value="tent">Tent</option>
          <option value="car">Car</option>
        </select>
        <select className="bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} data-testid="filter-zone">
          <option value="all">All Zones</option>
          {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
        </select>
        <select className="bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} data-testid="filter-channel">
          <option value="all">All Channels</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.method.toUpperCase()}</option>)}
        </select>
        <select className="bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status">
          <option value="all">All Statuses</option>
          <option value="unconfirmed">Unconfirmed</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div>
          <BrutalLabel>From</BrutalLabel>
          <BrutalInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-from" />
        </div>
        <div>
          <BrutalLabel>To</BrutalLabel>
          <BrutalInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-to" />
        </div>
      </BrutalCard>

      <BrutalCard className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead className="bg-[#FFB400]">
            <tr>
              {["Booking ID","Primary Camper","Type","Zone","Persons","Email","Mobile","Date","Channel","Status","Action"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest border-b-2 border-[#3D1E6D]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((b, i) => {
              const p = b.persons?.[0] || {};
              return (
                <tr key={b.id} className={`cursor-pointer hover:bg-[#FFB400]/30 ${i % 2 ? "bg-[#FFF4E6]" : "bg-white"}`} onClick={() => openDetails(b)} data-testid={`booking-row-${b.id}`}>
                  <td className="px-3 py-2 font-bold">{b.booking_id || b.id?.slice(0,8)}</td>
                  <td className="px-3 py-2">{p.name}</td>
                  <td className="px-3 py-2 uppercase">{b.camping_type}</td>
                  <td className="px-3 py-2">{b.zone_name}</td>
                  <td className="px-3 py-2">{b.persons.length}</td>
                  <td className="px-3 py-2 text-[#6B4E9E]">{p.email || b.user_email}</td>
                  <td className="px-3 py-2 text-[#6B4E9E]">{p.mobile_number || b.user_mobile}</td>
                  <td className="px-3 py-2">{new Date(b.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2">{channelLabel(b.payment_channel_id)}</td>
                  <td className="px-3 py-2"><Badge color={STATUS_COLORS[b.payment_status]}>{b.payment_status}</Badge></td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button onClick={() => openDetails(b)} className="kf-border bg-white p-1.5 hover:bg-[#FFB400]" data-testid={`view-${b.id}`}><Eye className="h-3 w-3" /></button>
                      <button onClick={() => setDeleteTarget(b)} className="kf-border bg-[#EF4444] text-white p-1.5" data-testid={`delete-${b.id}`}><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-[#6B4E9E]">No bookings match.</td></tr>}
          </tbody>
        </table>
      </BrutalCard>

      {/* DETAILS MODAL */}
      {selected && (
        <Modal onClose={() => setSelected(null)} title={`Booking ${selected.booking_id}`}>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <Field label="Primary Camper" value={selected.persons?.[0]?.name} />
            <Field label="Zone" value={selected.zone_name} />
            <Field label="Type" value={selected.camping_type} />
            <Field label="Persons" value={selected.persons.length} />
            <Field label="Email" value={selected.persons?.[0]?.email || selected.user_email} />
            <Field label="Mobile" value={selected.persons?.[0]?.mobile_number || selected.user_mobile} />
            <Field label="Check-in" value={selected.check_in} />
            <Field label="Check-out" value={selected.check_out} />
            <Field label="Booking Date" value={new Date(selected.created_at).toLocaleString()} />
            <Field label="Status" value={<Badge color={STATUS_COLORS[selected.payment_status]}>{selected.payment_status}</Badge>} />
            <Field label="Total" value={peso(selected.total_amount)} />
            <Field label="Passcode" value={selected.passcode} />
          </div>
          <hr className="my-4 border-[#3D1E6D]/30" />
          <h4 className="font-bold uppercase tracking-widest text-xs mb-2">Payment Details</h4>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <Field label="Channel" value={channelLabel(selected.payment_channel_id)} />
            <Field label="Method" value={selected.payment_method} />
            <Field label="Amount Paid" value={selected.amount_paid ? peso(selected.amount_paid) : "-"} />
            <Field label="Transaction Date" value={selected.transaction_date} />
            <Field label="Reference #" value={selected.reference_number} />
          </div>
          {selected.screenshot_base64 && (
            <div className="mt-3">
              <BrutalLabel>Payment Screenshot</BrutalLabel>
              <img src={selected.screenshot_base64} alt="Payment" className="kf-border max-h-72" data-testid="payment-screenshot" />
            </div>
          )}
          {selected.payment_status === "cancelled" && (
            <div className="mt-3 kf-border bg-[#FFB400] p-3 text-sm">
              <b>Cancellation reason:</b> {selected.cancellation_reason}<br />
              <span className="text-xs">on {selected.cancelled_at && new Date(selected.cancelled_at).toLocaleString()}</span>
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            <BrutalButton variant="outline" onClick={() => setContactOpen(true)} data-testid="contact-camper-btn"><Send className="h-4 w-4" /> Contact Camper</BrutalButton>
            {selected.payment_status !== "cancelled" && selected.payment_status !== "confirmed" && (
              <BrutalButton variant="primary" onClick={() => setConfirmOpen(true)} data-testid="confirm-payment-btn"><CheckCircle className="h-4 w-4" /> Confirm Payment</BrutalButton>
            )}
            {selected.payment_status !== "cancelled" && (
              <BrutalButton variant="dark" onClick={() => setCancelOpen(true)} data-testid="cancel-reservation-btn"><XCircle className="h-4 w-4" /> Cancel Reservation</BrutalButton>
            )}
          </div>
        </Modal>
      )}

      {contactOpen && selected && (
        <Modal onClose={() => setContactOpen(false)} title="Contact Camper">
          <div className="space-y-3 text-sm">
            <div><b>To:</b> {selected.persons?.[0]?.name} · {selected.user_email}</div>
            <div><b>Booking:</b> {selected.booking_id}</div>
            <div>
              <BrutalLabel>Subject</BrutalLabel>
              <BrutalInput value={contactForm.subject} onChange={(e) => setContactForm({...contactForm, subject: e.target.value})} data-testid="contact-subject" />
            </div>
            <div>
              <BrutalLabel>Message</BrutalLabel>
              <BrutalTextarea value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} data-testid="contact-message" />
            </div>
            <div className="flex gap-2 justify-end">
              <BrutalButton variant="outline" onClick={() => setContactOpen(false)}>Cancel</BrutalButton>
              <BrutalButton variant="primary" onClick={sendContact} data-testid="contact-send"><Send className="h-4 w-4" /> Send</BrutalButton>
            </div>
          </div>
        </Modal>
      )}

      {confirmOpen && selected && (
        <Modal onClose={() => setConfirmOpen(false)} title="Confirm Payment?">
          <p className="text-sm">Are you sure you want to confirm payment for <b>{selected.booking_id}</b>? This will issue QR codes for all campers (and car if applicable).</p>
          <div className="mt-4 flex gap-2 justify-end">
            <BrutalButton variant="outline" onClick={() => setConfirmOpen(false)}>No</BrutalButton>
            <BrutalButton variant="primary" onClick={confirmPayment} data-testid="confirm-payment-yes"><CheckCircle className="h-4 w-4" /> Yes, Confirm</BrutalButton>
          </div>
        </Modal>
      )}

      {cancelOpen && selected && (
        <Modal onClose={() => setCancelOpen(false)} title="Cancel Reservation">
          <div className="space-y-3 text-sm">
            <div><b>Camper:</b> {selected.persons?.[0]?.name}</div>
            <div><b>Booking:</b> {selected.booking_id}</div>
            <div><b>Email:</b> {selected.user_email}</div>
            <div>
              <BrutalLabel>Subject</BrutalLabel>
              <BrutalInput value={cancelForm.subject} onChange={(e) => setCancelForm({...cancelForm, subject: e.target.value})} data-testid="cancel-subject" />
            </div>
            <div>
              <BrutalLabel>Reason for Cancellation</BrutalLabel>
              <BrutalTextarea value={cancelForm.reason} onChange={(e) => setCancelForm({...cancelForm, reason: e.target.value})} data-testid="cancel-reason" />
            </div>
            <div className="flex gap-2 justify-end">
              <BrutalButton variant="outline" onClick={() => setCancelOpen(false)}>Close</BrutalButton>
              <BrutalButton variant="dark" onClick={cancelReservation} data-testid="cancel-now"><XCircle className="h-4 w-4" /> Cancel Now</BrutalButton>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal onClose={() => setDeleteTarget(null)} title="Delete booking?">
          <p className="text-sm">Are you sure you want to delete <b>{deleteTarget.booking_id}</b>? This will invalidate QR codes and mark it cancelled for the camper.</p>
          <div className="mt-4 flex gap-2 justify-end">
            <BrutalButton variant="outline" onClick={() => setDeleteTarget(null)}>No</BrutalButton>
            <BrutalButton variant="dark" onClick={() => deleteBooking(deleteTarget)} data-testid="delete-confirm"><Trash2 className="h-4 w-4" /> Delete</BrutalButton>
          </div>
        </Modal>
      )}

      {addOpen && <AddBookingModal zones={zones} channels={channels} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
    </div>
  );
}

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
    <div className="bg-white kf-border kf-shadow w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="modal">
      <div className="flex items-center justify-between mb-4">
        <h3 className="display text-2xl">{title}</h3>
        <button onClick={onClose}><X className="h-5 w-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-[#6B4E9E] font-bold">{label}</div>
    <div className="font-bold">{value || "-"}</div>
  </div>
);

function AddBookingModal({ zones, channels, onClose, onSaved }) {
  const [form, setForm] = useState({
    zone_id: zones[0]?.id || "", camping_type: "tent",
    check_in: "", check_out: "",
    payment_status: "unconfirmed", payment_method: "", payment_channel_id: "",
    amount_paid: "", reference_number: "",
    car_details: { plate_number: "", model: "", color: "" },
  });
  const [persons, setPersons] = useState([{ firstname: "", lastname: "", middlename: "", suffix: "", birthdate: "", age: 0, email: "", mobile_number: "" }]);

  const updateP = (i, k, v) => {
    const c = [...persons];
    c[i] = { ...c[i], [k]: v };
    setPersons(c);
  };

  const submit = async () => {
    if (!form.zone_id || !form.check_in || !form.check_out) return toast.error("Zone & dates required");
    if (!persons[0].firstname || !persons[0].email) return toast.error("Primary camper required");
    try {
      const { data } = await api.post("/bookings/admin-create", {
        zone_id: form.zone_id, camping_type: form.camping_type,
        check_in: form.check_in, check_out: form.check_out,
        persons, car_details: form.camping_type === "car" ? form.car_details : null,
        payment_status: form.payment_status, payment_method: form.payment_method || null,
        payment_channel_id: form.payment_channel_id || null,
        amount_paid: form.amount_paid ? parseFloat(form.amount_paid) : null,
        reference_number: form.reference_number || null,
      });
      toast.success(`Created ${data.booking_id} — passcode ${data.passcode}. Booking details email sent to ${persons[0].email}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal title="Add Booking (Manual)" onClose={onClose}>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div>
          <BrutalLabel>Zone</BrutalLabel>
          <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={form.zone_id} onChange={(e) => setForm({...form, zone_id: e.target.value})} data-testid="add-zone">
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
        </div>
        <div>
          <BrutalLabel>Camping Type</BrutalLabel>
          <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={form.camping_type} onChange={(e) => setForm({...form, camping_type: e.target.value})} data-testid="add-type">
            <option value="tent">Tent</option><option value="car">Car</option>
          </select>
        </div>
        <div><BrutalLabel>Check-in</BrutalLabel><BrutalInput type="date" value={form.check_in} onChange={(e) => setForm({...form, check_in: e.target.value})} data-testid="add-checkin" /></div>
        <div><BrutalLabel>Check-out</BrutalLabel><BrutalInput type="date" value={form.check_out} onChange={(e) => setForm({...form, check_out: e.target.value})} data-testid="add-checkout" /></div>
        <div>
          <BrutalLabel>Payment Status</BrutalLabel>
          <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={form.payment_status} onChange={(e) => setForm({...form, payment_status: e.target.value})} data-testid="add-status">
            <option value="unconfirmed">Unconfirmed</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <BrutalLabel>Payment Channel</BrutalLabel>
          <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={form.payment_channel_id} onChange={(e) => {
            const c = channels.find((x) => x.id === e.target.value);
            setForm({...form, payment_channel_id: e.target.value, payment_method: c?.method || ""});
          }} data-testid="add-channel">
            <option value="">—</option>
            {channels.map((c) => <option key={c.id} value={c.id}>{c.method.toUpperCase()} · {c.account_name}</option>)}
          </select>
        </div>
        <div><BrutalLabel>Amount Paid</BrutalLabel><BrutalInput type="number" value={form.amount_paid} onChange={(e) => setForm({...form, amount_paid: e.target.value})} data-testid="add-amount" /></div>
        <div><BrutalLabel>Reference Number</BrutalLabel><BrutalInput value={form.reference_number} onChange={(e) => setForm({...form, reference_number: e.target.value})} data-testid="add-ref" /></div>
      </div>

      <h4 className="display text-xl mt-4 mb-2">Primary Camper</h4>
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div><BrutalLabel>First Name *</BrutalLabel><BrutalInput value={persons[0].firstname} onChange={(e) => updateP(0, "firstname", e.target.value)} data-testid="add-p-firstname" /></div>
        <div><BrutalLabel>Middle Name</BrutalLabel><BrutalInput value={persons[0].middlename} onChange={(e) => updateP(0, "middlename", e.target.value)} /></div>
        <div><BrutalLabel>Last Name *</BrutalLabel><BrutalInput value={persons[0].lastname} onChange={(e) => updateP(0, "lastname", e.target.value)} data-testid="add-p-lastname" /></div>
        <div><BrutalLabel>Birthdate</BrutalLabel><BrutalInput type="date" value={persons[0].birthdate} onChange={(e) => updateP(0, "birthdate", e.target.value)} /></div>
        <div><BrutalLabel>Email *</BrutalLabel><BrutalInput type="email" value={persons[0].email} onChange={(e) => updateP(0, "email", e.target.value)} data-testid="add-p-email" /></div>
        <div><BrutalLabel>Mobile</BrutalLabel><BrutalInput value={persons[0].mobile_number} onChange={(e) => updateP(0, "mobile_number", e.target.value)} data-testid="add-p-mobile" /></div>
      </div>

      <div className="mt-5 flex gap-2 justify-end">
        <BrutalButton variant="outline" onClick={onClose}>Cancel</BrutalButton>
        <BrutalButton variant="primary" onClick={submit} data-testid="add-save"><Save className="h-4 w-4" /> Create Booking</BrutalButton>
      </div>
    </Modal>
  );
}
